import { get, post } from "./client.js";

export type UserRow = {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  createdActorType: string | null;
  createdActorId: string | null;
  status: "unverified" | "verified" | "rejected";
  metadata: Record<string, unknown> | null;
  externalId: string | null;
};

export type UsersResponse = {
  data: UserRow[];
  meta: { total: number; limit: number; offset: number };
};

export type Firm = {
  id: string;
  name: string;
  firmRef: string | null;
};

export type FirmsSearchResult = {
  data: Firm[];
  meta: { total: number; limit: number; offset: number };
};

export const firms = {
  search: async (name: string, offset = 0): Promise<FirmsSearchResult> => {
    const params = new URLSearchParams({ search: name, limit: "50", offset: String(offset) });
    return get<FirmsSearchResult>(`/v1/organisations/search?${params.toString()}`);
  },
};

export type FronteggUserAccount = {
  id: string;
  name: string;
  roles: { id: string; name: string }[];
  lastVisit: string;
  visits: number;
  isMainAccount: boolean;
  invitationToken: string | null;
  invitationTokenExpirationDate: string | null;
  status: string;
  subAccountAccessLimit: number;
};

export type FronteggUserEntry = {
  id: string;
  sub: string;
  name: string;
  email: string;
  profilePictureUrl: string;
  verified: boolean;
  phoneNumber: string | null;
  provider: string;
  mfaEnrolled: boolean;
  /** JSON-encoded string — parse before use */
  metadata: string | null;
  vendorMetadata: string | null;
  tenantId: string;
  tenantIds: string[];
  tenants: { tenantId: string; temporaryExpirationDate: string | null; isDisabled: boolean }[];
  createdAt: string;
  isLocked: boolean;
  managedBy: string;
  subAccountAccessAllowed: boolean;
  externalId: string | null;
  activatedForTenant: boolean;
  accounts: FronteggUserAccount[];
};

export type FronteggUsersResponse = {
  items: FronteggUserEntry[];
  _metadata?: {
    totalItems: number;
    totalPages: number;
  };
};

export type VerifyUserResponse = {
  userId: string;
  organisationId: string;
  firmName: string;
  firmRef: string | null;
};

export const users = {
  list: async (params: {
    search?: string;
    status?: string;
    createdActorId?: string;
    limit: number;
    offset: number;
  }): Promise<UsersResponse> => {
    const searchParams = new URLSearchParams({
      limit: String(params.limit),
      offset: String(params.offset),
    });
    if (params.search) searchParams.set("search", params.search);
    if (params.status) searchParams.set("status", params.status);
    if (params.createdActorId) searchParams.set("created_actor_id", params.createdActorId);
    return get<UsersResponse>(`/v1/users?${searchParams.toString()}`);
  },
};

export const fronteggUsers = {
  verify: async (userId: string, organisationId: string): Promise<VerifyUserResponse> => {
    return post(`/v1/frontegg-users/${encodeURIComponent(userId)}/verify`, {
      organisationId,
    });
  },
  reject: async (userId: string): Promise<{ userId: string }> => {
    return post(`/v1/frontegg-users/${encodeURIComponent(userId)}/reject`, {});
  },
};
